/* eslint-disable no-param-reassign */
import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { captureException } from '@sentry/react'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { Button } from '../../../components/common/Button'
import { IconChoices } from '../../../components/common/Icon'
import { theme } from '../../../theme/theme'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IBoardRoomEventModel } from '../../../interfaces/models/boardroom-events.interfaces'
import {
  IEventAttendanceStatus,
  IEventModel,
} from '../../../interfaces/models/events.interfaces'
import { eventAttendance } from '../../../api/events'
import { boardRoomEventAttendance } from '../../../api/boardroom-events'
import { FormControl } from '../../../components/common/FormControl'
import { TextInput } from '../../../components/common/TextInput'

interface EventAttendanceButtonProps {
  label: string
  active: boolean
  backgroundColorClassName: string
  onClick: () => void
  icon?: IconChoices
  iconColor?: string
  loading?: boolean
  disabled?: boolean
}

export const EventAttendanceButton = ({
  label,
  active,
  backgroundColorClassName,
  onClick,
  icon,
  iconColor,
  loading,
  disabled,
}: EventAttendanceButtonProps): React.ReactNode => {
  return (
    <Button
      iconColor={iconColor || theme.colors.white}
      icon={icon}
      label={label}
      className={`
      bg-transparent border-${backgroundColorClassName} border-1
      hover:bg-${backgroundColorClassName}
      hover:bg-opacity-70
      ${active ? `bg-${backgroundColorClassName}` : ''}`}
      loading={loading}
      onClick={onClick}
      disabled={disabled}
    />
  )
}

interface EventAttendanceButtonsProps {
  event: IEventModel | IBoardRoomEventModel
  boardRoomEvent: boolean | undefined
}

export const EventAttendanceButtons = ({
  event,
  boardRoomEvent,
}: EventAttendanceButtonsProps): JSX.Element => {
  const userId = useAuthenticatedUserId() as string
  const { translate } = useAppTranslation()

  const userAttendance = event?.attendance?.find(
    (attendance) => attendance.userId === userId
  )
  const { control, watch, formState, reset } = useForm({
    mode: 'all',
    defaultValues: { comment: userAttendance?.comment || '' },
  })
  const userAttending = userAttendance?.status === 'attending'
  const userNotAttending = userAttendance?.status === 'not_attending'

  const { mutate: updateEventAttendance, isPending: updatingEventAttendance } =
    useMutation({
      mutationFn: async ({
        id,
        status,
        comment,
      }: {
        status: IEventAttendanceStatus
        id: string
        comment: string
      }) => {
        return (await eventAttendance(id, { status, comment })).data.data
      },
      onSuccess: (data) => {
        if (data) {
          event.attendance = data.attendance
        }
        reset({ comment: watch('comment') })
      },
      onError: (err) => {
        captureException(err)
      },
    })

  const {
    mutate: boardRoomUpdateEventAttendance,
    isPending: boardRoomUpdatingEventAttendance,
  } = useMutation({
    mutationFn: async ({
      id,
      status,
      comment,
    }: {
      status: IEventAttendanceStatus
      id: string
      comment: string
    }) => {
      return (await boardRoomEventAttendance(id, { status, comment })).data.data
    },
    onSuccess: (data) => {
      if (data) {
        event.attendance = data.attendance
      }
      reset({ comment: watch('comment') })
    },
    onError: (err) => {
      captureException(err)
    },
  })
  const loading = boardRoomEvent
    ? boardRoomUpdatingEventAttendance
    : updatingEventAttendance

  const isUpdatingAttending = formState.isDirty && userAttending
  const isUpdatingNotAttending = formState.isDirty && userNotAttending
  const disableAttendingButton = (): boolean => {
    if (loading) return true
    if (isUpdatingAttending) return false
    if (userAttending) return true
    return false
  }
  const disableNotAttendingButton = (): boolean => {
    if (loading) return true
    if (isUpdatingNotAttending) return false
    if (userNotAttending) return true
    return false
  }
  const onEventAttendanceAttending = (): void => {
    if (boardRoomEvent) {
      boardRoomUpdateEventAttendance({
        id: event._id,
        status: 'attending',
        comment: watch('comment'),
      })
    } else {
      updateEventAttendance({
        id: event._id,
        status: 'attending',
        comment: watch('comment'),
      })
    }
  }

  const onEventAttendanceNotAttending = (): void => {
    if (boardRoomEvent) {
      boardRoomUpdateEventAttendance({
        id: event._id,
        status: 'not_attending',

        comment: watch('comment'),
      })
    } else {
      updateEventAttendance({
        id: event._id,
        status: 'not_attending',
        comment: watch('comment'),
      })
    }
  }
  return (
    <div>
      <Controller
        control={control}
        name="comment"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            name={name}
            className="grow"
            label={translate('common.eventActions.attendance.comment')}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={loading}
              className="max-w-120 mb-2"
            />
          </FormControl>
        )}
      />
      <div className="flex gap-2">
        <EventAttendanceButton
          label={
            isUpdatingAttending
              ? translate('common.eventActions.attendance.updateComment')
              : translate('common.eventActions.attendance.attending')
          }
          icon={IconChoices.CHECKMARK}
          iconColor={theme.colors.brandGreen}
          active={userAttending}
          backgroundColorClassName="brandGreen"
          loading={loading}
          disabled={disableAttendingButton()}
          onClick={onEventAttendanceAttending}
        />
        <EventAttendanceButton
          label={
            isUpdatingNotAttending
              ? translate('common.eventActions.attendance.updateComment')
              : translate('common.eventActions.attendance.notAttending')
          }
          icon={IconChoices.CROSS}
          iconColor={theme.colors.red}
          active={userNotAttending}
          backgroundColorClassName="red"
          loading={loading}
          disabled={disableNotAttendingButton()}
          onClick={onEventAttendanceNotAttending}
        />
      </div>
    </div>
  )
}
